import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import {Document, Page, pdfjs} from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import './PdfViewer.scss';
import {InDocumentSearchProps, PdfHighlight} from './PdfViewer.types';
import {highlightElement, scrollToElementFromContent, scrollToElementFromHighlight} from './ViewerUtils';
import {useDispatch} from 'react-redux';
import {TransformComponent, TransformWrapper, useControls} from 'react-zoom-pan-pinch';
import {
    useStateCurrentPageNumber,
    useStateCurrentPageRotation,
    useStateDocumentQueryHighlights,
    useStateDoResetTransform,
    useStateHighlightId,
    useStateScrollTarget,
} from '../../search/search-hooks';
import {
    resetTransformAction,
    setCurrentPageNumberAction,
    setCurrentPageRotationAction,
    setDocumentAllPagesRotation,
    setDocumentQueryHighlights,
    setDocumentSelectedTags,
    setHighlightIdAction,
    setSelectedDocumentNumberOfPages,
} from '../../search/duck/search-actions';
import {ClickableElement} from '../../search/search-results/search-result-details/content/clickable-section-list/clickableSectionList';
import {computePageRotation} from '../../utils/utils';
import {PagesController} from '../pages-controller/PagesController';
import {Spinner} from '../spinner/spinner';

// Avoid fetching pdf.js worker - IN-1320 - Fix from: https://github.com/mozilla/pdf.js/issues/8305#issuecomment-518674409
// And set workerSrc correctly for pdfjs-dist v4.x
pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).toString();

interface PdfViewerProps {
    file?: Blob;
    isLoading: boolean;
    onInDocumentSearchPropsChange: (inDocumentSearchProps: InDocumentSearchProps) => void;
}

const areHighlightsDifferent = (a: PdfHighlight, b: PdfHighlight): boolean =>
    a.page !== b.page ||
    JSON.stringify(a.rect) !== JSON.stringify(b.rect) ||
    JSON.stringify(a.contentsObj) !== JSON.stringify(b.contentsObj) ||
    JSON.stringify(a.titleObj) !== JSON.stringify(b.titleObj);

const areHighlightListsDifferent = (list1: PdfHighlight[], list2: PdfHighlight[]): boolean =>
    list1.length !== list2.length || list1.some((highlight, index) => areHighlightsDifferent(highlight, list2[index]));

export const PdfViewer = memo(({file, isLoading, onInDocumentSearchPropsChange}: PdfViewerProps) => {
    const queryHighlights = useStateDocumentQueryHighlights();
    const highlightId = useStateHighlightId(); // tag or query highlight
    const pageNumber = useStateCurrentPageNumber();
    const pageRotation = useStateCurrentPageRotation();
    const refPreviewPageScroll: React.RefObject<any> = useRef(null);
    const scrollTarget: ClickableElement | null = useStateScrollTarget();
    const dispatch = useDispatch();
    const [width, setWidth] = useState(800);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
    const [percentage, setPercentage] = useState<number>(100);
    const [scale, setScale] = useState<number>(1);

    const onQueryHighlightIndexChange = useCallback(
        (queryHighlightIndex: number) => {
            if (queryHighlights.length > 0) {
                setHighlightedIndex(queryHighlightIndex);
                const queryHighlight = queryHighlights[queryHighlightIndex];
                dispatch(setCurrentPageNumberAction(queryHighlight.page));
                dispatch(setHighlightIdAction(queryHighlight.id));
            }
        },
        [dispatch, queryHighlights],
    );

    useEffect(() => {
        const data: InDocumentSearchProps = {
            numHighlights: queryHighlights.length,
            highlightedIndex: highlightedIndex,
            onHighlightIndexChange: onQueryHighlightIndexChange,
            onSearchTriggered: onSearchTriggered,
        };
        onInDocumentSearchPropsChange(data);
    }, [highlightedIndex, onQueryHighlightIndexChange, onInDocumentSearchPropsChange, queryHighlights.length]);

    useEffect(() => {
        const resizeObserver = new ResizeObserver((event) => {
            // Resize observer error, see: https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
            window.requestAnimationFrame(() => {
                // Depending on the layout, you may need to swap inlineSize with blockSize
                // https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserverEntry/contentBoxSize
                const newWidth = event[0].contentBoxSize[0].inlineSize;
                newWidth !== width && setWidth(event[0].contentBoxSize[0].inlineSize);
            });
        });
        const stage = document.getElementById('hibuPdfPreview');
        if (stage) {
            resizeObserver.observe(stage);
        }
    });

    function handleKeyDown(event) {
        if (event.key === 'Alt' || event.key === 'Control') {
            const hibuPdfPreviewPageScroll: HTMLDivElement | undefined = refPreviewPageScroll.current ?? undefined;
            if (hibuPdfPreviewPageScroll) {
                hibuPdfPreviewPageScroll.classList.add('key-pressed');
            }
        }
    }

    function handleKeyUp(event) {
        if (event.key === 'Alt' || event.key === 'Control') {
            const hibuPdfPreviewPageScroll: HTMLDivElement | undefined = refPreviewPageScroll.current ?? undefined;
            if (hibuPdfPreviewPageScroll) {
                hibuPdfPreviewPageScroll.classList.remove('key-pressed');
            }
        }
    }

    function handleMouseDown() {
        const hibuPdfPreviewPageScroll: HTMLDivElement | undefined = refPreviewPageScroll.current ?? undefined;
        if (hibuPdfPreviewPageScroll) {
            hibuPdfPreviewPageScroll.classList.add('mouse-down');
        }
    }

    function handleMouseUp() {
        const hibuPdfPreviewPageScroll: HTMLDivElement | undefined = refPreviewPageScroll.current ?? undefined;
        if (hibuPdfPreviewPageScroll) {
            hibuPdfPreviewPageScroll.classList.remove('mouse-down');
        }
    }

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);

    const scrollToContent = async () => {
        if (scrollTarget !== null) {
            scrollToElementFromContent(scrollTarget, refPreviewPageScroll);
        }
    };

    const scrollToHighlight = () => {
        isElementLoaded('[data-annotation-id=\'' + highlightId + '\']').then((htmlElement) => {
            if (htmlElement) {
                scrollToElementFromHighlight(htmlElement, refPreviewPageScroll);
                highlightElement(htmlElement);
            }
        });
    };

    const isElementLoaded = async (selector: string) => {
        while (document.querySelector(selector) === null) {
            await new Promise((resolve) => requestAnimationFrame(resolve));
        }
        return document.querySelector(selector);
    };

    const onSearchTriggered = () => {
        setHighlightedIndex(0);
    };

    function handleZoomIn() {
        dispatch(resetTransformAction(false));
        const newScale = scale >= 1 ? scale + 0.25 : scale + 0.1;
        setScale(newScale);
        setPercentage(Math.floor(newScale * 100));
    }

    function handleZoomOut() {
        dispatch(resetTransformAction(false));
        if (scale >= 0.3) {
            const newScale = scale <= 1 ? scale - 0.1 : scale - 0.25;
            setScale(newScale);
            setPercentage(Math.floor(newScale * 100));
        }
    }

    const onDocumentLoadSuccess = useCallback(
        async (document: any) => {
            const newQueryHighlights: PdfHighlight[] = [];
            const selectedTags: PdfHighlight[] = [];
            const pageRotations: number[] = [];
            for (let i = 1; i <= document.numPages; i++) {
                const page = await document.getPage(i);
                const annotations = await page.getAnnotations();
                pageRotations[i - 1] = page.rotate;
                annotations.forEach((hl) => {
                    if (hl.subtype === 'Highlight') {
                        if (hl.contentsObj?.str.startsWith('HIBU query term')) {
                            newQueryHighlights.push({page: i, ...hl});
                        } else if (hl.contentsObj?.str.startsWith('HIBU tag')) {
                            selectedTags.push({page: i, ...hl});
                        }
                    }
                });
            }
            dispatch(setDocumentAllPagesRotation(pageRotations));
            dispatch(setSelectedDocumentNumberOfPages(document.numPages));
            dispatch(setDocumentSelectedTags(selectedTags));
            dispatch(setDocumentQueryHighlights(newQueryHighlights));
            dispatch(setHighlightIdAction(newQueryHighlights[highlightedIndex]?.id));

            if (pageNumber < 1 || pageNumber > document.numPages) {
                dispatch(setCurrentPageNumberAction(1));
            }

            // Update annotations only when a new document is loaded
            // otherwise component is re-rendered and currentPage set back to 1 when we are just switching tabs
            const initialHighlight: PdfHighlight | undefined =
                newQueryHighlights.length > 0 ? newQueryHighlights[0] : undefined;
            if (areHighlightListsDifferent(newQueryHighlights, queryHighlights)) {
                dispatch(setCurrentPageNumberAction(initialHighlight?.page));
                dispatch(setHighlightIdAction(initialHighlight?.id));
            }
        },
        [dispatch, highlightedIndex, pageNumber, queryHighlights],
    );

    if (!file) {
        return null;
    }

    const HibuPdfPreviewPageScroll = ({
        scrollTarget,
        highlightId,
    }: {
        scrollTarget: ClickableElement | null;
        highlightId: string | undefined;
    }) => {
        const {resetTransform} = useControls();
        const doResetTransform = useStateDoResetTransform();

        // reset panning
        useEffect(() => {
            if (doResetTransform) {
                resetTransform();
            }
        }, [doResetTransform, resetTransform]);

        const handleScrolling = useCallback(() => {
            if (scrollTarget) {
                // handle jumps to toc elements and sections
                scrollToContent();
            } else if (highlightId) {
                // handle jumps to query highlights and tag highlights
                scrollToHighlight();
            }
        }, [scrollTarget, highlightId]);

        // handle changes within a page
        useEffect(() => {
            handleScrolling();
        }, [handleScrolling]);

        // handle changes across pages
        const onPageRenderSuccess = () => {
            handleScrolling();
        };

        function handleResetZoomAndPan() {
            setScale(1);
            setPercentage(100);
            setTimeout(() => {
                resetTransform(); //reset panning, setTimout needed because of timing issue
            }, 0);
        }

        function handlePageRotation(rotation: number) {
            dispatch(setCurrentPageRotationAction(computePageRotation(pageRotation, rotation)));
            handleResetZoomAndPan();
        }

        return (
            <>
                <div id={'hibuPdfPreviewPageScroll'} ref={refPreviewPageScroll}>
                    <div id={'hibuPdfPreviewPage'}>
                        <TransformComponent>
                            <Page
                                pageNumber={pageNumber}
                                renderAnnotationLayer={true}
                                width={width}
                                loading={''}
                                onRenderSuccess={() => onPageRenderSuccess()}
                                scale={scale}
                                rotate={pageRotation}
                            />
                        </TransformComponent>
                    </div>
                </div>
                <PagesController
                    percentage={percentage}
                    handleZoomIn={handleZoomIn}
                    handleZoomOut={handleZoomOut}
                    handleResetZoomAndPan={handleResetZoomAndPan}
                    handlePageRotation={handlePageRotation}
                    showPagesButtons={true}
                />
            </>
        );
    };

    return (
        <div id={'hibuPdfPreview'}>
            {isLoading ? (
                <Spinner/>
            ) : (
                <Document file={file} onLoadSuccess={onDocumentLoadSuccess} loading={''}>
                    <div id={'hibuPdfPreviewPageWrapper'}>
                        <TransformWrapper
                            wheel={{disabled: true}}
                            doubleClick={{disabled: true}}
                            limitToBounds={false}
                            panning={{activationKeys: ['Control', 'Alt']}}
                        >
                            <HibuPdfPreviewPageScroll scrollTarget={scrollTarget} highlightId={highlightId} />
                        </TransformWrapper>
                    </div>
                </Document>
            )}
        </div>
    );
});
